import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { usePageTitleContext } from "@/contexts/PageTitleContext";
import { useTenant } from "@/contexts/TenantContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Clock,
  CheckCircle,
  DollarSign,
  AlertTriangle,
  Layers,
  Filter,
  FileText,
  BarChart3,
  TrendingUp,
  Brain,
  Zap,
  ListFilter,
  Target,
  Users,
  Calendar,
} from "lucide-react";
import ScriptIntelligence from "@/components/ScriptIntelligence";
import MetaDrilldown from "@/components/MetaDrilldown";
import { BenchmarkStatusBadge, BenchmarkIndicator, StatusFilterSelect } from "@/components/BenchmarkIndicator";
import VerticalFilter from "@/components/VerticalFilter";
import ProductMixPanel from "@/components/ProductMixPanel";
import RevenueForecastPanel from "@/components/RevenueForecastPanel";
import OperatorChecklist from "@/components/OperatorChecklist";
import OperatorStatusIndicator from "@/components/OperatorStatusIndicator";
import TrendCharts from "@/components/TrendCharts";
import ForecastAccuracy from "@/components/ForecastAccuracy";
import SystemHealthBadge from "@/components/SystemHealthBadge";
import BottleneckBadge from "@/components/BottleneckBadge";
import DataIntegrityBadge from "@/components/DataIntegrityBadge";
import { useChannelOptions, useVerticalOptions } from "@/hooks/useVocabulary";
import { useWarRoomMetrics } from "@/hooks/useWarRoomMetrics";

// Extracted War Room components
import {
  KPICard,
  pct,
  currency,
  ProfitPulse,
  ProfitPulseCompact,
  FunnelEconomicsWaterfall,
} from "@/components/warroom";
import { FunnelVisualization } from "@/components/warroom/FunnelVisualization";
import { AgentLeaderboard } from "@/components/warroom/AgentLeaderboard";
// MA-028: Legacy CampaignPerformance removed — replaced by CanonicalCampaignAnalytics
import CanonicalCampaignAnalytics from "@/components/warroom/CanonicalCampaignAnalytics";
import { ScriptPerformance } from "@/components/warroom/ScriptPerformance";
import ProfitPaths from "@/components/warroom/ProfitPaths";
import WarRoomFunnelsView from "@/components/warroom/WarRoomFunnelsView";
import { OptimizationOpportunities } from "@/components/warroom/OptimizationOpportunities";
import { ComplianceHealthCard } from "@/components/warroom/ComplianceHealthCard";  // MA-COMPLIANCE-VISIBILITY-002
import { ComplianceImpactSection } from "@/components/warroom/ComplianceImpactSection";  // MA-UI-107
// MA-IMPL-120: Phase 5 Analytics & Intelligence Layer
// MA-028: Legacy CampaignRevenueAnalytics removed — replaced by CanonicalCampaignAnalytics
import BottleneckAlerts from "@/components/warroom/BottleneckAlerts";
// MA-IMPL-125: Script & Journey Analytics
import ScriptAnalytics from "@/components/warroom/ScriptAnalytics";
// MA-IMPL-126B: Message Variant Analytics
import MessageVariantAnalytics from "@/components/warroom/MessageVariantAnalytics";
// MA-AN-129: Message Intelligence (Category-Level Performance)
import MessageIntelligence from "@/components/warroom/MessageIntelligence";
// MA-ARCH-131: Impulse Alignment Analytics
import ImpulseAlignmentCard from "@/components/warroom/ImpulseAlignmentCard";
// MA-ARCH-134: Message Optimization Recommendations
import OptimizationRecommendations from "@/components/warroom/OptimizationRecommendations";
// MA-ARCH-136A: Helper Text for Decision Clarity
import { SectionHeader, WarningBanner, HELPER_DEFINITIONS } from "@/components/warroom/HelperText";
// MA-ARCH-137: Action Layer Components
// MA-PHASE11A-DEFER-021: Actions feature deferred to future release
// import ActionableRecommendations from "@/components/warroom/ActionableRecommendations";
// import ActionQueue from "@/components/warroom/ActionQueue";
import AgentPerformanceAnalytics from "@/components/warroom/AgentPerformanceAnalytics";  // MA-ARCH-152
import WarRoomCoverageBanner from "@/components/warroom/WarRoomCoverageBanner";  // MA-ARCH-181-WR-BANNER
import EmailDashboardPanel from "@/components/warroom/EmailDashboardPanel";  // Email Dashboard shell (Checkpoint 1)
import WarRoomPrimaryNav from "@/components/warroom/WarRoomPrimaryNav";  // 5-group navigation consolidation (Checkpoint 2)
import { getStoredUser } from "@/lib/auth";

export default function AnalyticsWarRoom() {
  // Checkpoint 1 War Room visual refinement: suppress the shared
  // PageTitleCard hero (logo/account-name/large-title, rendered by
  // AppShell above every page's content) for this route only. The War
  // Room section selector below is this module's own primary heading, so
  // the hero is redundant here. Uses only the context's existing public
  // setTitle/setSource API (the same API usePageTitle itself calls) — no
  // shared file is modified, and title behavior is restored on unmount so
  // no state leaks into whatever route is visited next.
  const pageTitleCtx = usePageTitleContext();
  useEffect(() => {
    if (!pageTitleCtx) return;
    pageTitleCtx.setTitle("");
    pageTitleCtx.setSource("custom");
    return () => {
      pageTitleCtx.setSource("default");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // MA-ARCH-137: Get current user for action approvals
  const user = getStoredUser();
  
  // P0 TENANT ISOLATION: Subscribe to tenant context for cross-tenant viewing
  const { selectedTenantId, isViewingOtherTenant, isSuperAdmin } = useTenant();
  
  // P1 VOCABULARY: Use vocabulary hooks for filters
  const { options: channelOptions } = useChannelOptions();
  const { options: verticalVocabOptions } = useVerticalOptions();

  // URL query params for filter persistence
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Initialize state from URL params (with defaults)
  const [period, setPeriod] = useState(() => searchParams.get("period") || "mtd");
  const [channel, setChannel] = useState(() => searchParams.get("channel") || "all");
  const [workspaceId, setWorkspaceId] = useState(() => searchParams.get("workspace_id") || "");
  const [campaignId, setCampaignId] = useState(() => searchParams.get("campaign_id") || "all");
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(() => searchParams.get("tab") || "overview");
  const [activeMetaSubTab, setActiveMetaSubTab] = useState("revenue"); // MA-IMPL-120: Default to Revenue Analytics

  // MA-BE-082: Use canonical War Room metrics hook for KPIs
  // MA-ANALYTICS-001: Pass tenantId for SuperAdmin cross-tenant viewing
  // MA-IMPL-118: Pass campaignId for campaign-scoped analytics
  // MA-PHASE11A-POSTLOGIN-ROUTING-FIX-011: Only enable when selectedTenantId exists
  const {
    loading: metricsLoading,
    error: metricsError,
    kpis,
    funnel,
    summary,
    waterfall,
    refresh: refreshMetrics
  } = useWarRoomMetrics({
    period,
    workspaceId: workspaceId || undefined,
    campaignId: campaignId || undefined,
    tenantId: selectedTenantId || undefined,  // MA-ANALYTICS-001: SuperAdmin tenant override
    enabled: !!selectedTenantId  // MA-PHASE11A: Prevent premature API calls
  });

  const [warRoomCampaigns, setWarRoomCampaigns] = useState([]); // MA-028: Canonical campaigns for filter dropdown
  const [bookingAgents, setBookingAgents] = useState([]);
  const [salesAgents, setSalesAgents] = useState([]);
  const [scripts, setScripts] = useState([]);
  const [benchmarks, setBenchmarks] = useState({}); // KPI benchmarks from /evaluate
  const [vertical, setVertical] = useState(() => searchParams.get("vertical") || "all"); // Vertical filter
  const [verticalsList, setVerticalsList] = useState([]); // Available verticals (from API for counts)
  const [workspaces, setWorkspaces] = useState([]); // Available workspaces

  // Update URL when filters change
  // P0 TENANT ISOLATION: Preserve tenant_id when updating other params
  // MA-IMPL-118: Include campaign_id in URL params
  useEffect(() => {
    setSearchParams((prev) => {
      const params = new URLSearchParams();
      
      // P0 TENANT ISOLATION: Always preserve tenant_id from current params
      const tenantId = prev.get("tenant_id");
      if (tenantId) {
        params.set("tenant_id", tenantId);
      }
      
      // Set filter params
      if (period && period !== "mtd") params.set("period", period);
      if (channel && channel !== "all") params.set("channel", channel);
      if (workspaceId) params.set("workspace_id", workspaceId);
      if (campaignId && campaignId !== "all") params.set("campaign_id", campaignId);
      if (vertical && vertical !== "all") params.set("vertical", vertical);
      if (activeTab && activeTab !== "overview") params.set("tab", activeTab);
      
      return params;
    }, { replace: true });
  }, [period, channel, workspaceId, campaignId, vertical, activeTab, setSearchParams]);

  // Refs for scroll targets
  const kpisRef = useRef(null);
  const forecastRef = useRef(null);
  const verticalFilterRef = useRef(null);
  const productMixRef = useRef(null);
  const operatorSectionRef = useRef(null);
  const tabsListRef = useRef(null);
  const revenueContainerRef = useRef(null);

  // Handler for tab change with scroll for Operator
  const handleTabChange = useCallback((newTab) => {
    setActiveTab(newTab);
    if (newTab === 'operator') {
      // Auto-scroll so the tab list is at the top of viewport
      setTimeout(() => {
        tabsListRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, []);

  // Handler for Operator checklist navigation
  const handleOperatorNavigate = useCallback((tab, anchor) => {
    // Map view_target to actual tabs
    const tabMap = {
      'overview': 'overview',
      'campaigns': 'campaigns',
      'meta': 'campaigns',
      'scripts': 'scripts',
      'revenue': 'revenue',
    };

    const targetTab = tabMap[tab] || 'overview';
    setActiveTab(targetTab);

    // For meta drilldown, also set the sub-tab
    if (tab === 'meta') {
      setActiveMetaSubTab('meta');
    }

    // Schedule scroll after tab change renders
    setTimeout(() => {
      let targetRef = null;
      switch (anchor) {
        case 'kpis':
          targetRef = kpisRef;
          break;
        case 'forecast':
          // Scroll to revenue container for forecast panel
          targetRef = revenueContainerRef;
          break;
        case 'accuracy':
          // Scroll to revenue container for forecast accuracy
          targetRef = revenueContainerRef;
          break;
        case 'bottleneck':
          // Bottleneck is now a badge in header - just navigate to overview
          targetRef = kpisRef;
          break;
        case 'vertical_filter':
          targetRef = verticalFilterRef;
          // Also set the vertical to mortgage_protection for daily check
          setVertical('mortgage_protection');
          break;
        case 'product_mix':
          targetRef = productMixRef;
          break;
        case 'summary':
          // Script Intelligence summary - just navigate to scripts tab
          break;
        case 'drilldown':
          // Meta drilldown - tab is already set
          break;
        default:
          break;
      }

      if (targetRef?.current) {
        targetRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 150);
  }, [setActiveTab, setActiveMetaSubTab, setVertical]);

  // Fetch verticals list, workspaces, and campaigns for filters on mount
  // MA-PHASE11A-POSTLOGIN-ROUTING-FIX-011: Only fetch when selectedTenantId exists
  useEffect(() => {
    if (!selectedTenantId) return; // Prevent premature API calls
    
    const fetchVerticals = async () => {
      try {
        const res = await api.get('/analytics/verticals/list');
        setVerticalsList(res.data?.verticals || []);
      } catch (e) {
        console.error("Failed to load verticals:", e);
      }
    };
    
    const fetchWorkspaces = async () => {
      try {
        const res = await api.get('/tenants/workspaces');
        setWorkspaces(res.data || []);
      } catch (e) {
        console.error("Failed to load workspaces:", e);
      }
    };
    
    // MA-CAMPAIGN-SURFACE-ALIGNMENT-028: Fetch canonical campaigns for filter dropdown
    const fetchWarRoomCampaigns = async () => {
      try {
        const res = await api.get('/marketing-campaigns?limit=200');
        const items = (res.data?.items || []).map(c => ({
          id: c.id,
          name: c.name,
          source_platform: c.source_platform,
        }));
        setWarRoomCampaigns(items);
      } catch (e) {
        console.error("Failed to load canonical campaigns for filter:", e);
      }
    };
    
    fetchVerticals();
    fetchWorkspaces();
    fetchWarRoomCampaigns();
  }, [selectedTenantId, workspaceId]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      // Build query params - PHASE 5D: Use channel instead of view
      const verticalParam = vertical && vertical !== "all" ? `&vertical=${vertical}` : "";
      const channelParam = channel && channel !== "all" ? `&channel=${channel}` : "";
      const workspaceParam = workspaceId ? `&workspace_id=${workspaceId}` : "";
      const campaignParam = campaignId && campaignId !== "all" ? `&campaign_id=${campaignId}` : "";
      
      // MA-BE-082: KPIs now come from useWarRoomMetrics hook (canonical endpoint)
      // MA-CAMPAIGN-SURFACE-ALIGNMENT-028: Removed legacy /analytics/campaigns fetch
      const [ba, sa, sp, bm] = await Promise.all([
        api.get(`/analytics/booking-agents?period=${period}${verticalParam}${workspaceParam}${campaignParam}`),
        api.get(`/analytics/sales-agents?period=${period}${verticalParam}${workspaceParam}${campaignParam}`),
        api.get(`/analytics/scripts/performance?period=${period}${campaignParam}`),
        api.get(`/analytics/benchmarks/evaluate?format=structured${verticalParam}${campaignParam}`),
      ]);

      setBookingAgents(ba.data?.booking_agents || []);
      setSalesAgents(sa.data?.sales_agents || []);
      setScripts(sp.data?.scripts || []);
      
      // Convert benchmark array to map keyed by KPI name
      const benchmarkMap = {};
      for (const kpi of bm.data?.kpis || []) {
        benchmarkMap[kpi.kpi] = kpi;
      }
      setBenchmarks(benchmarkMap);
    } catch (e) {
      toast({
        title: "Failed to load analytics",
        description: e?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedTenantId) return; // MA-PHASE11A: Prevent premature API calls
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, vertical, channel, workspaceId, campaignId, selectedTenantId]);

  // MA-BE-082: KPIs and funnel now come from useWarRoomMetrics hook
  // (removed legacy: const kpis = warroom?.kpis || {}; const funnel = warroom?.funnel || [];)

  return (
    <div className="space-y-6" data-testid="warroom-page">
      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-4">
        {/* War Room primary navigation (Checkpoint 2): the original 12 peer
            triggers are consolidated into 5 primary groups, rendered by
            WarRoomPrimaryNav. activeTab/handleTabChange and every
            TabsContent panel below are unchanged — only how each
            destination is reached changed. */}
        <div ref={tabsListRef} data-testid="warroom-tabs-list" className="w-full">
          <WarRoomPrimaryNav
            activeTab={activeTab}
            onNavigate={handleTabChange}
            helperDefinitions={HELPER_DEFINITIONS}
          />
        </div>

        {activeTab !== 'email' && (
        <div data-testid="warroom-executive-controls" className="space-y-6">
      {/* Period and Filter Selectors */}
      <Card className="glass border-white/10">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-300">Period:</span>
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="w-[140px] bg-white/5 text-slate-100 border-white/10" data-testid="period-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7d">Last 7 Days</SelectItem>
                  <SelectItem value="30d">Last 30 Days</SelectItem>
                  <SelectItem value="mtd">Month to Date</SelectItem>
                  <SelectItem value="ytd">Year to Date</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Channel Filter (Phase 5D) - P1 VOCABULARY: Use vocabulary options */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-300">Channel:</span>
              <Select value={channel} onValueChange={setChannel}>
                <SelectTrigger className="w-[160px] bg-white/5 text-slate-100 border-white/10" data-testid="channel-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Channels</SelectItem>
                  {channelOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Workspace Filter (Phase 5) */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-300">Workspace:</span>
              <Select value={workspaceId || "all"} onValueChange={(val) => setWorkspaceId(val === "all" ? "" : val)}>
                <SelectTrigger className="w-[160px] bg-white/5 text-slate-100 border-white/10" data-testid="workspace-select">
                  <SelectValue placeholder="All Workspaces" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Workspaces</SelectItem>
                  {workspaces.map((ws) => (
                    <SelectItem key={ws.id} value={ws.id}>
                      {ws.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* MA-IMPL-118: Campaign Filter */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-300">Campaign:</span>
              <Select value={campaignId || "all"} onValueChange={setCampaignId}>
                <SelectTrigger className="w-[180px] bg-white/5 text-slate-100 border-white/10" data-testid="campaign-select">
                  <SelectValue placeholder="All Campaigns" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  <SelectItem value="all" className="text-slate-100">All Campaigns</SelectItem>
                  {warRoomCampaigns.map((camp) => (
                    <SelectItem key={camp.id} value={camp.id} className="text-slate-100">
                      {camp.name}{camp.source_platform ? ` (${camp.source_platform})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* P1 VOCABULARY: Use vocabulary options for vertical filter */}
            <div className="flex items-center gap-2" ref={verticalFilterRef}>
              <span className="text-sm text-slate-300">Vertical:</span>
              <Select value={vertical || "all"} onValueChange={setVertical}>
                <SelectTrigger 
                  className="w-[180px] h-9 text-sm bg-white/5 border-white/10 text-slate-100" 
                  data-testid="vertical-filter-trigger"
                >
                  <SelectValue placeholder="All Verticals" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  <SelectItem value="all" className="text-slate-100">All Verticals</SelectItem>
                  {verticalVocabOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value} className="text-slate-100">
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={fetchAll}
              disabled={loading}
              variant="outline"
            >
              {loading ? "Loading..." : "Refresh"}
            </Button>
            
            {/* Spacer to push badges to the right */}
            <div className="flex-1" />
            
            {/* Profit Pulse Compact - MA-BE-082, MA-IMPL-118: includes campaign filter */}
            <ProfitPulseCompact period={period} campaignId={campaignId !== 'all' ? campaignId : undefined} />
            
            {/* Data Integrity Badge - P1 Hardening */}
            <DataIntegrityBadge />
            
            {/* Bottleneck Badge */}
            <BottleneckBadge 
              period={period}
              vertical={vertical}
            />
            
            {/* System Health Badge */}
            <SystemHealthBadge 
              vertical={vertical}
            />

            {/* Operator Status Indicator */}
            <OperatorStatusIndicator 
              onNavigateToOperator={() => handleTabChange('operator')}
            />
          </div>
        </CardContent>
      </Card>

      {/* MA-ARCH-181-WR-BANNER: Attribution Coverage Banner */}
      <WarRoomCoverageBanner
        period={period}
        campaignId={campaignId !== "all" ? campaignId : undefined}
        workspaceId={workspaceId || undefined}
      />

      {/* ===================================================================== */}
      {/* MA-IMPL-118B: WAR ROOM 4-LAYER RESTRUCTURE                          */}
      {/* ===================================================================== */}

      {/* ===== LAYER A: EXECUTIVE LAYER (Decision KPIs) ===== */}
      <div className="space-y-2">
        <h3 className="text-xs font-medium text-slate-400 uppercase tracking-wider">Executive Summary</h3>
        <div ref={kpisRef} className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <KPICard
            title="Net Profit"
            value={kpis.net_profit?.value}
            format="currency"
            change={kpis.net_profit?.change_percent}
            direction={kpis.net_profit?.direction}
            status={kpis.net_profit?.status}
            reason={kpis.net_profit?.reason}
            benchmark={benchmarks.net_profit}
            icon={DollarSign}
            className="ring-1 ring-emerald-500/30"
          />
          <KPICard
            title="Commission"
            value={kpis.commission_generated?.value}
            format="currency"
            change={kpis.commission_generated?.change_percent}
            direction={kpis.commission_generated?.direction}
            status={kpis.commission_generated?.status}
            reason={kpis.commission_generated?.reason}
            icon={DollarSign}
          />
          <KPICard
            title="Capital Efficiency"
            value={kpis.revenue_per_lead?.value ? (kpis.commission_generated?.value / (kpis.cost_per_lead?.value * kpis.leads_generated?.value || 1)) : null}
            format="number"
            suffix="x"
            change={kpis.revenue_per_lead?.change_percent}
            direction={kpis.revenue_per_lead?.direction}
            status={kpis.revenue_per_lead?.status}
            reason="Revenue return on ad spend"
            icon={Target}
          />
          <KPICard
            title="Ad Spend"
            value={kpis.cost_per_lead?.value && kpis.leads_generated?.value ? kpis.cost_per_lead.value * kpis.leads_generated.value : null}
            format="currency"
            change={kpis.cost_per_lead?.change_percent}
            direction={kpis.cost_per_lead?.direction === 'up' ? 'down' : 'up'}
            status={kpis.cost_per_lead?.status}
            reason="Total marketing spend"
            icon={DollarSign}
            isLowerBetter
          />
          <KPICard
            title="Deployable Rate"
            value={kpis.deployable_rate?.value}
            format="percent"
            change={kpis.deployable_rate?.change_percent}
            direction={kpis.deployable_rate?.direction}
            status={kpis.deployable_rate?.status}
            reason={kpis.deployable_rate?.reason || "% leads eligible for outreach"}
            icon={CheckCircle}
          />
        </div>
      </div>

      {/* ===== LAYER B: CONVERSION FLOW ===== */}
      {/* MA-CLEANUP-121: Refactored into counts-first structure */}
      <div className="space-y-4">
        {/* PRIMARY FLOW: Counts (the pipeline) */}
        <div className="space-y-2">
          <h3 className="text-xs font-medium text-slate-400 uppercase tracking-wider">Pipeline (Counts)</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <KPICard
              title="Leads"
              value={kpis.leads_generated?.value}
              format="number"
              change={kpis.leads_generated?.change_percent}
              direction={kpis.leads_generated?.direction}
              status={kpis.leads_generated?.status}
              reason={kpis.leads_generated?.reason}
              icon={Users}
              className="ring-1 ring-blue-500/20"
            />
            <KPICard
              title="Appointments"
              value={kpis.appointments_count?.value}
              format="number"
              change={kpis.appointments_count?.change_percent}
              direction={kpis.appointments_count?.direction}
              status={kpis.appointments_count?.status}
              reason="Appointments scheduled"
              icon={Calendar}
              className="ring-1 ring-blue-500/20"
            />
            <KPICard
              title="Shows"
              value={kpis.shows_count?.value}
              format="number"
              change={kpis.shows_count?.change_percent}
              direction={kpis.shows_count?.direction}
              status={kpis.shows_count?.status}
              reason="Appointments where lead showed"
              icon={CheckCircle}
              className="ring-1 ring-blue-500/20"
            />
            <KPICard
              title="Clients"
              value={kpis.clients_count?.value}
              format="number"
              change={kpis.clients_count?.change_percent}
              direction={kpis.clients_count?.direction}
              status={kpis.clients_count?.status}
              reason="Total clients won"
              icon={Users}
              className="ring-1 ring-blue-500/20"
            />
            <KPICard
              title="Revenue"
              value={kpis.commission_generated?.value}
              format="currency"
              change={kpis.commission_generated?.change_percent}
              direction={kpis.commission_generated?.direction}
              status={kpis.commission_generated?.status}
              reason="Total commission generated"
              icon={DollarSign}
              className="ring-1 ring-emerald-500/30"
            />
          </div>
        </div>
        
        {/* SECONDARY METRICS: Rates & Efficiency */}
        <div className="space-y-2">
          <h3 className="text-xs font-medium text-slate-400 uppercase tracking-wider">Conversion Rates</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <KPICard
              title="Lead → Appt"
              value={kpis.lead_to_booked_rate?.value}
              format="percent"
              change={kpis.lead_to_booked_rate?.change_percent}
              direction={kpis.lead_to_booked_rate?.direction}
              status={kpis.lead_to_booked_rate?.status}
              reason="Booking conversion rate"
              benchmark={benchmarks.lead_to_booked_rate}
              icon={TrendingUp}
            />
            <KPICard
              title="Appt → Show"
              value={kpis.show_rate?.value}
              format="percent"
              change={kpis.show_rate?.change_percent}
              direction={kpis.show_rate?.direction}
              status={kpis.show_rate?.status}
              reason={kpis.show_rate?.reason}
              benchmark={benchmarks.show_rate}
              icon={TrendingUp}
            />
            <KPICard
              title="Show → Close"
              value={kpis.sales_conversion_rate?.value}
              format="percent"
              change={kpis.sales_conversion_rate?.change_percent}
              direction={kpis.sales_conversion_rate?.direction}
              status={kpis.sales_conversion_rate?.status}
              reason="Sales conversion rate"
              benchmark={benchmarks.sales_conversion_rate}
              icon={TrendingUp}
            />
            <KPICard
              title="Rev / Lead"
              value={kpis.revenue_per_lead?.value}
              format="currency"
              change={kpis.revenue_per_lead?.change_percent}
              direction={kpis.revenue_per_lead?.direction}
              status={kpis.revenue_per_lead?.status}
              reason={kpis.revenue_per_lead?.reason}
              benchmark={benchmarks.revenue_per_lead}
              icon={DollarSign}
            />
            <KPICard
              title="Rev / Appt"
              value={kpis.revenue_per_appointment?.value}
              format="currency"
              change={kpis.revenue_per_appointment?.change_percent}
              direction={kpis.revenue_per_appointment?.direction}
              status={kpis.revenue_per_appointment?.status}
              reason="Revenue per appointment"
              icon={DollarSign}
            />
          </div>
        </div>
      </div>

      {/* ===== LAYER C: INPUT + COMPLIANCE ===== */}
      <div className="space-y-2">
        <h3 className="text-xs font-medium text-slate-400 uppercase tracking-wider">Input & Compliance</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <KPICard
            title="CPL"
            value={kpis.cost_per_lead?.value}
            format="currency"
            change={kpis.cost_per_lead?.change_percent}
            direction={kpis.cost_per_lead?.direction}
            status={kpis.cost_per_lead?.status}
            reason={kpis.cost_per_lead?.reason}
            benchmark={benchmarks.cpl}
            icon={DollarSign}
            isLowerBetter
          />
          <KPICard
            title="CPA"
            value={kpis.cost_per_acquisition?.value}
            format="currency"
            change={kpis.cost_per_acquisition?.change_percent}
            direction={kpis.cost_per_acquisition?.direction}
            status={kpis.cost_per_acquisition?.status}
            reason={kpis.cost_per_acquisition?.reason}
            benchmark={benchmarks.cpa}
            icon={DollarSign}
            isLowerBetter
          />
          <KPICard
            title="Speed to Book"
            value={kpis.speed_to_book_median_hours?.value}
            format="hours"
            suffix="hrs"
            change={kpis.speed_to_book_median_hours?.change_percent}
            direction={kpis.speed_to_book_median_hours?.direction}
            status={kpis.speed_to_book_median_hours?.status}
            reason={kpis.speed_to_book_median_hours?.reason}
            benchmark={benchmarks.speed_to_book_hours}
            icon={Clock}
            isLowerBetter
          />
          <KPICard
            title="Premium"
            value={kpis.premium_written?.value}
            format="currency"
            change={kpis.premium_written?.change_percent}
            direction={kpis.premium_written?.direction}
            status={kpis.premium_written?.status}
            reason={kpis.premium_written?.reason}
            icon={DollarSign}
          />
        </div>
      </div>

      {/* 30-Day Rolling Trend Lines - Directly under KPI cards */}
      {/* MA-IMPL-118B: Pass campaign filter to TrendCharts */}
      <TrendCharts
        vertical={vertical}
        campaignId={campaignId !== 'all' ? campaignId : undefined}
        className="mt-4"
      />
        </div>
        )}

        <TabsContent value="overview">
          {/* MA-ARCH-137: Actionable Recommendations (replaces OptimizationRecommendations) */}
          {/* MA-PHASE11A-DEFER-021: Actions feature deferred
          <div className="mb-6">
            <ActionableRecommendations
              period={period}
              campaignId={campaignId !== 'all' ? campaignId : undefined}
              workspaceId={workspaceId || undefined}
              channel={channel !== 'all' ? channel : undefined}
              vertical={vertical !== 'all' ? vertical : undefined}
              tenantId={selectedTenantId}
              maxRecommendations={3}
              userId={user?.id || 'unknown'}
            />
          </div>
          */}
          
          {/* MA-IMPL-118B: Removed Profit Pulse widget - consolidated into Executive Layer KPIs */}
          
          {/* MA-UI-107: Compliance Impact Section */}
          <div className="mt-2">
            <ComplianceImpactSection
              period={period}
              channel={channel !== 'all' ? channel : null}
              campaignId={campaignId !== 'all' ? campaignId : null}
              tenantId={selectedTenantId}
            />
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-6">
            {/* Funnel Economics Waterfall (MA-BACKLOG-051) */}
            <div className="lg:col-span-6">
              <FunnelEconomicsWaterfall 
                data={waterfall}
                loading={metricsLoading}
                error={metricsError}
              />
            </div>
            {/* MA-IMPL-118B: Removed CampaignPerformance duplicate - moved to Campaigns tab only */}
            {/* Optimization Opportunities (MA-AI-OPT-001) + Compliance Health + Bottleneck Alerts */}
            <div className="lg:col-span-6 space-y-4">
              {/* MA-IMPL-120: Bottleneck Detection Alerts */}
              <BottleneckAlerts 
                period={period}
                workspaceId={workspaceId || undefined}
                tenantId={selectedTenantId}
              />
              <OptimizationOpportunities />
              {/* MA-COMPLIANCE-VISIBILITY-002: TrustedForm Capture Health */}
              <ComplianceHealthCard 
                period={period}
                campaignId={campaignId !== 'all' ? campaignId : undefined}
                tenantId={selectedTenantId}
              />
            </div>
          </div>
          
          {/* Product Mix Panel - Shows when a vertical is selected */}
          {vertical && vertical !== "all" && (
            <div className="mt-6" ref={productMixRef}>
              <ProductMixPanel 
                vertical={vertical}
                period={period}
                token={localStorage.getItem('token')}
              />
            </div>
          )}
        </TabsContent>

        {/* MA-CAMPAIGN-SURFACE-ALIGNMENT-028: Canonical Campaign Analytics */}
        <TabsContent value="campaigns">
          <Tabs value={activeMetaSubTab} onValueChange={setActiveMetaSubTab} className="space-y-4">
            <TabsList className="bg-white/5 border-white/10">
              <TabsTrigger value="revenue" className="data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-300" data-testid="campaign-revenue-tab">
                <DollarSign className="h-3.5 w-3.5 mr-1.5" />
                Campaign Performance
              </TabsTrigger>
              <TabsTrigger value="meta" className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-300" data-testid="meta-drilldown-tab">
                <Layers className="h-3.5 w-3.5 mr-1.5" />
                Meta Drilldown
              </TabsTrigger>
            </TabsList>

            {/* Canonical Campaign Analytics — powered by /profit-path-analytics/campaigns */}
            <TabsContent value="revenue" data-testid="campaign-revenue-content">
              <CanonicalCampaignAnalytics
                period={period}
                campaignId={campaignId !== 'all' ? campaignId : undefined}
                tenantId={selectedTenantId}
              />
            </TabsContent>

            {/* Meta Drilldown — isolated platform-specific diagnostic (raw Meta IDs) */}
            <TabsContent value="meta" data-testid="meta-drilldown-content">
              <MetaDrilldown 
                period={period} 
                vertical={vertical}
                campaignId={campaignId !== 'all' ? campaignId : undefined}
              />
            </TabsContent>
          </Tabs>
        </TabsContent>

        {/* Funnels Tab (MA-UI-023) */}
        <TabsContent value="funnels" data-testid="funnels-tab-content">
          <WarRoomFunnelsView 
            period={period}
            campaignId={campaignId !== 'all' ? campaignId : undefined}
            tenantId={selectedTenantId}
          />
        </TabsContent>

        <TabsContent value="booking">
          <AgentLeaderboard
            title="Booking Agent Leaderboard"
            agents={bookingAgents}
            type="booking"
            benchmarks={benchmarks}
          />
        </TabsContent>

        <TabsContent value="sales">
          <AgentLeaderboard
            title="Sales Agent Leaderboard"
            agents={salesAgents}
            type="sales"
            benchmarks={benchmarks}
          />
        </TabsContent>

        {/* MA-ARCH-152: Unified Agent Performance Analytics */}
        <TabsContent value="agents" data-testid="agents-tab-content">
          <AgentPerformanceAnalytics
            period={period}
            campaignId={campaignId !== 'all' ? campaignId : undefined}
            vertical={vertical !== 'all' ? vertical : undefined}
            channel={channel !== 'all' ? channel : undefined}
          />
        </TabsContent>

        <TabsContent value="scripts" data-testid="scripts-tab-content">
          <Tabs defaultValue="analytics" className="space-y-4">
            <TabsList className="bg-white/5 border-white/10">
              <TabsTrigger value="analytics" className="data-[state=active]:bg-purple-500/20 data-[state=active]:text-purple-300" data-testid="script-analytics-tab">
                <FileText className="h-3.5 w-3.5 mr-1.5" />
                Script & Journey
              </TabsTrigger>
              <TabsTrigger value="message-variants" className="data-[state=active]:bg-pink-500/20 data-[state=active]:text-pink-300" data-testid="message-variant-tab">
                <TrendingUp className="h-3.5 w-3.5 mr-1.5" />
                Message Variants
              </TabsTrigger>
              <TabsTrigger value="message-intelligence" className="data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-300" data-testid="message-intelligence-tab">
                <Brain className="h-3.5 w-3.5 mr-1.5" />
                Message Intelligence
              </TabsTrigger>
              <TabsTrigger value="impulse-alignment" className="data-[state=active]:bg-violet-500/20 data-[state=active]:text-violet-300" data-testid="impulse-alignment-tab">
                <Zap className="h-3.5 w-3.5 mr-1.5" />
                Impulse Alignment
              </TabsTrigger>
              <TabsTrigger value="intelligence" className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-300">
                <BarChart3 className="h-3.5 w-3.5 mr-1.5" />
                Script Intelligence
              </TabsTrigger>
            </TabsList>

            {/* MA-IMPL-125: Script & Journey Analytics using lineage chain */}
            <TabsContent value="analytics" data-testid="script-analytics-content">
              <ScriptAnalytics
                period={period}
                workspaceId={workspaceId || undefined}
                campaignId={campaignId !== 'all' ? campaignId : undefined}
                vertical={vertical !== 'all' ? vertical : undefined}
                channel={channel !== 'all' ? channel : undefined}
                tenantId={selectedTenantId}
              />
            </TabsContent>

            {/* MA-IMPL-126B: Message Variant Analytics using lineage chain */}
            <TabsContent value="message-variants" data-testid="message-variant-content">
              <MessageVariantAnalytics
                period={period}
                workspaceId={workspaceId || undefined}
                campaignId={campaignId !== 'all' ? campaignId : undefined}
                vertical={vertical !== 'all' ? vertical : undefined}
                channel={channel !== 'all' ? channel : undefined}
                tenantId={selectedTenantId}
              />
            </TabsContent>

            {/* MA-AN-129: Message Intelligence (Category-Level Performance) */}
            <TabsContent value="message-intelligence" data-testid="message-intelligence-content">
              <MessageIntelligence
                period={period}
                workspaceId={workspaceId || undefined}
                campaignId={campaignId !== 'all' ? campaignId : undefined}
                vertical={vertical !== 'all' ? vertical : undefined}
                channel={channel !== 'all' ? channel : undefined}
                tenantId={selectedTenantId}
              />
            </TabsContent>

            {/* MA-ARCH-131: Impulse Alignment Analytics */}
            <TabsContent value="impulse-alignment" data-testid="impulse-alignment-content">
              <ImpulseAlignmentCard
                period={period}
                workspaceId={workspaceId || undefined}
                campaignId={campaignId !== 'all' ? campaignId : undefined}
                profitPathId={undefined}
                vertical={vertical !== 'all' ? vertical : undefined}
                channel={channel !== 'all' ? channel : undefined}
                tenantId={selectedTenantId}
              />
            </TabsContent>

            <TabsContent value="intelligence">
              <ScriptIntelligence 
                period={period}
                campaignId={campaignId !== 'all' ? campaignId : undefined}
              />
            </TabsContent>
          </Tabs>
        </TabsContent>

        <TabsContent value="revenue" data-testid="revenue-tab-content">
          <div ref={revenueContainerRef}>
            {/* Forecast Accuracy at the top - MA-IMPL-118B: Pass campaign filter */}
            <ForecastAccuracy 
              campaignId={campaignId !== 'all' ? campaignId : undefined}
              className="mb-6" 
            />
            
            {/* Revenue Forecast Panel - MA-IMPL-118B: Pass campaign filter */}
            <div ref={forecastRef}>
              <RevenueForecastPanel 
                period={period}
                vertical={vertical}
                campaignId={campaignId !== 'all' ? campaignId : undefined}
              />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="profit-paths" data-testid="profit-paths-tab-content">
          <ProfitPaths 
            period={period}
            campaignId={campaignId !== 'all' ? campaignId : undefined}
            workspaceId={workspaceId || undefined}
            tenantId={selectedTenantId}
          />
        </TabsContent>

        <TabsContent value="operator" data-testid="operator-tab-content">
          <div ref={operatorSectionRef}>
            <OperatorChecklist 
              onNavigate={handleOperatorNavigate}
            />
          </div>
        </TabsContent>

        {/* MA-ARCH-137: Actions Tab */}
        <TabsContent value="actions" data-testid="actions-tab-content">
          <div className="space-y-6">
            {/* MA-PHASE11A-DEFER-021: Actions feature deferred to future release */}
            <div className="text-center py-12 text-slate-400">
              <p className="text-lg">Actions feature coming soon</p>
              <p className="text-sm mt-2">This feature is currently under development</p>
            </div>
            {/* 
            <ActionableRecommendations
              period={period}
              campaignId={campaignId !== 'all' ? campaignId : undefined}
              workspaceId={workspaceId || undefined}
              channel={channel !== 'all' ? channel : undefined}
              vertical={vertical !== 'all' ? vertical : undefined}
              tenantId={selectedTenantId}
              maxRecommendations={10}
              userId={user?.id || 'unknown'}
            />
            
            <ActionQueue workspaceId={workspaceId || undefined} />
            */}
          </div>
        </TabsContent>

        {/* Email Dashboard shell (Checkpoint 1) */}
        <TabsContent value="email" data-testid="email-tab-content">
          <EmailDashboardPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}
